import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import './AssetUploadModal.css';
import { Modal, Button, Alert } from '../../common';
import { assetApi } from '../../../services/apis/configuredApis';

function AssetUploadModal({ show, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  
  const {
    handleSubmit,
    reset,
  } = useForm();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file || null);
  };

  // Helper function to convert File to ArrayBuffer then to base64
  const fileToBase64ArrayBuffer = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = () => {
        // Convert ArrayBuffer to base64
        const bytes = new Uint8Array(reader.result);
        const binary = bytes.reduce((acc, byte) => acc + String.fromCharCode(byte), '');
        const base64 = btoa(binary);
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError(null);
      
      if (!selectedFile) {
        setError('Please select a file to upload');
        setLoading(false);
        return;
      }

      // Read file content as base64
      let fileContentBase64;
      try {
        // Use ArrayBuffer method for better binary handling
        fileContentBase64 = await fileToBase64ArrayBuffer(selectedFile);
      } catch (err) {
        console.error('Error reading file:', err);
        setError('Failed to read file content');
        setLoading(false);
        return;
      }

      // Prepare asset data with file content
      const apiData = {
        file_name: selectedFile.name,
        file_size_bytes: selectedFile.size,
        mime_type: selectedFile.type || 'application/octet-stream',
        storage_key: `assets/${Date.now()}-${selectedFile.name}`,
        file_content: fileContentBase64,
        asset_type: 'lead_magnet',
      };

      await assetApi.assetCreatePost({
        assetCreate: apiData,
      });
      
      // Close modal and notify parent of success
      onClose();
      reset();
      setSelectedFile(null);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error uploading asset:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to upload asset';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      reset();
      setSelectedFile(null);
      setError(null);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={show}
      onClose={handleClose}
      title="Upload New Asset"
      footer={
        <div className="">
          <Button
            variant="secondary"
            onClick={handleClose}
            type="button"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            type="button"
            disabled={loading || !selectedFile}
          >
            {loading ? 'Uploading...' : 'Upload'}
          </Button>
        </div>
      }
    >
      {error && (
        <Alert
          variant="error"
          onClose={() => setError(null)}
          className="asset-upload-modal__alert"
        >
          {error}
        </Alert>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="asset-upload-modal__field">
          <label htmlFor="file">
            Select File (PDF, ZIP, DOC, etc.) <span className="asset-upload-modal__required">*</span>
          </label>
          <input
            id="file"
            type="file"
            accept=".pdf,.zip,.doc,.docx,.xls,.xlsx,.txt,.csv"
            onChange={handleFileChange}
            required
          />
          {selectedFile && (
            <p className="asset-upload-modal__file-info">
              Selected: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024).toFixed(2)} KB)
            </p>
          )}
          {!selectedFile && (
            <span className="asset-upload-modal__error">Please select a file to upload</span>
          )}
        </div>
      </form>
    </Modal>
  );
}

export default AssetUploadModal;

